import React, { useState, useMemo, useEffect, useDeferredValue } from 'react';
import { Modal, Form, Table, InputGroup, Pagination, Button } from 'react-bootstrap';

const ArticleSearchModal = ({ show, onHide, onSelectArticle, articles = [], onOpenImageModal }) => {
    const [search, setSearch] = useState('');
    const deferredSearch = useDeferredValue(search);
    const [page, setPage] = useState(1);
    const itemsPerPage = 8;

    // Reset state on show
    useEffect(() => {
        if (show) {
            setSearch('');
            setPage(1);
        }
    }, [show]);

    useEffect(() => {
        setPage(1);
    }, [deferredSearch]);

    const safeArticles = useMemo(() => {
        return Array.isArray(articles) ? articles : (articles?.results || []);
    }, [articles]);

    const filteredArticles = useMemo(() => {
        if (!deferredSearch) return safeArticles;
        const lower = deferredSearch.toLowerCase().trim();
        return safeArticles.filter(a => 
            (a.id && String(a.id).toLowerCase().includes(lower)) || 
            (a.name && a.name.toLowerCase().includes(lower))
        );
    }, [safeArticles, deferredSearch]);

    const paginatedArticles = useMemo(() => {
        const startIndex = (page - 1) * itemsPerPage;
        return filteredArticles.slice(startIndex, startIndex + itemsPerPage);
    }, [filteredArticles, page, itemsPerPage]);

    return (
        <Modal show={show} onHide={onHide} size="xl" centered>
            <Modal.Header closeButton>
                <Modal.Title><i className="bi bi-box-seam me-2"></i>Buscar Artículo</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <InputGroup className="mb-3">
                    <InputGroup.Text><i className="bi bi-search"></i></InputGroup.Text>
                    <Form.Control
                        placeholder="Buscar por Código o Descripción"
                        value={search}
                        onChange={(e) => {
                            setSearch(e.target.value);
                            setPage(1);
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                if (paginatedArticles.length > 0) {
                                    onSelectArticle(paginatedArticles[0]);
                                    onHide();
                                }
                            }
                        }}
                        autoFocus
                    />
                </InputGroup>
                
                <div style={{ minHeight: '400px' }}>
                    <Table striped bordered hover responsive size="sm" className="align-middle">
                        <thead className="table-light">
                            <tr>
                                <th style={{ width: '15%' }}>Código</th>
                                <th>Descripción</th>
                                <th style={{ width: '15%' }}>Precio</th>
                                <th style={{ width: '15%' }} className="text-center">Acción</th>
                            </tr>
                        </thead>
                        <tbody>
                            {paginatedArticles.map((a) => (
                                <tr 
                                    key={a.id} 
                                    onDoubleClick={() => {
                                        onSelectArticle(a);
                                        onHide();
                                    }}
                                    onTouchEnd={(e) => {
                                        const now = Date.now();
                                        const lastTap = e.currentTarget._lastTap || 0;
                                        if (now - lastTap < 300) {
                                            onSelectArticle(a);
                                            onHide();
                                        }
                                        e.currentTarget._lastTap = now;
                                    }}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <td className="fw-bold">{a.id}</td>
                                    <td>{a.name}</td>
                                    <td>${(a.price || 0).toFixed(2)}</td>
                                    <td className="text-center">
                                        <div className="d-flex align-items-center justify-content-center gap-1">
                                            {onOpenImageModal && (
                                                <Button 
                                                    variant="outline-info" 
                                                    size="sm" 
                                                    className="rounded-circle d-flex align-items-center justify-content-center shadow-sm"
                                                    style={{ width: '32px', height: '32px', padding: 0 }}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        onOpenImageModal(a);
                                                    }}
                                                    title="Ver imagen del artículo"
                                                >
                                                    <i className="bi bi-eye"></i>
                                                </Button>
                                            )}
                                            <Button 
                                                variant="outline-success" 
                                                size="sm" 
                                                className="shadow-sm"
                                                onClick={() => {
                                                    onSelectArticle(a);
                                                    onHide();
                                                }}
                                            >
                                                <i className="bi bi-cart-plus me-1"></i> Agregar
                                            </Button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {paginatedArticles.length === 0 && (
                                <tr>
                                    <td colSpan="4" className="text-center text-muted py-5">
                                        No se encontraron artículos coincidentes.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </div>

                {filteredArticles.length > itemsPerPage && (
                    <Pagination className="justify-content-center mb-0 mt-3">
                        <Pagination.Prev disabled={page === 1} onClick={() => setPage(page - 1)} />
                        {Array.from({ length: Math.ceil(filteredArticles.length / itemsPerPage) }, (_, i) => (
                            <Pagination.Item key={i + 1} active={i + 1 === page} onClick={() => setPage(i + 1)}>
                                {i + 1}
                            </Pagination.Item>
                        ))}
                        <Pagination.Next disabled={page === Math.ceil(filteredArticles.length / itemsPerPage)} onClick={() => setPage(page + 1)} />
                    </Pagination>
                )}
            </Modal.Body>
        </Modal>
    );
};

export default ArticleSearchModal;
